import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [
    { title: "金融・企業法務 - 顧問契約・訴訟・コンプライアンス支援 | 法務法人フィサン" },
    { name: "description", content: "韓国ソウルの金融・企業法務専門弁護士。金融機関、企業への包括的法務支援、顧問契約、訴訟対応、コンプライアンス、規制対応など日本企業のビジネスを日本語でサポート。" },
    { name: "keywords", content: "金融法務, 企業法務, 顧問弁護士, 企業訴訟, コンプライアンス, 金融規制, 投資紛争, 契約審査, 株主紛争, 韓国弁護士, 日本語対応, ソウル法律事務所" },
    { property: "og:title", content: "金融・企業法務 - 顧問契約・訴訟・コンプライアンス支援 | 法務法人フィサン" },
    { property: "og:description", content: "金融機関・企業への包括的法務支援、顧問契約、訴訟対応を日本語で提供。日韓ビジネスの橋渡し役として信頼されるパートナーです。" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://jp-hwisang.co.kr/ja/services/finance-corporate" },
    { property: "og:image", content: "https://jp-hwisang.co.kr/images/jp-hwisang-card.jpg" },
    { property: "og:locale", content: "ja_JP" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "robots", content: "index, follow" },
  ];
};

export default function FinanceCorporateDetail() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <a href="/ja" className="flex items-center justify-center md:justify-start">
              <img src="/images/no-padding-logo.png" alt="法務法人フィサン" className="h-8 w-auto opacity-90" />
            </a>

            <div className="flex items-center justify-center md:justify-end gap-4 md:gap-6">
              <nav className="flex items-center gap-4 md:gap-6 text-sm">
                <a href="/ja/office" className="text-gray-700 hover:text-black whitespace-nowrap">事務所・所在地</a>
                <a href="/ja/services" className="text-black font-medium whitespace-nowrap">業務分野</a>
                <a href="/ja/blog" className="text-gray-700 hover:text-black whitespace-nowrap">法律ブログ</a>
              </nav>
              <a
                href="https://jp-hwisang.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 border border-gray-300 hover:border-gray-900 text-gray-700 hover:text-black text-sm transition-colors whitespace-nowrap"
              >
                한국어
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <section className="py-8 px-4 border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <a href="/ja/services" className="inline-flex items-center text-sm text-gray-600 hover:text-black transition-colors">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            業務分野一覧に戻る
          </a>
        </div>
      </section>

      {/* Title Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-serif mb-6 text-gray-900">金融・企業顧問及び訴訟</h1>
          <div className="w-16 h-px bg-gray-400 mb-8"></div>
          <p className="text-xl text-gray-700 leading-relaxed">
            金融・企業法務 ― 専門チームによるワンストップ支援
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto space-y-12">
          <div>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              韓国でのビジネス活動には、法制度や商慣習の違いから、思わぬリスクや法的課題が生じることがあります。
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              輝相（フィサン）は、金融・企業分野に特化した専門チームを中心に、企業のあらゆる局面で発生する法律問題に対し、迅速かつ戦略的なワンストップサポートを提供しています。
            </p>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">金融機関・企業への包括的な法務支援</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                当事務所は、銀行・保険会社・投資機関などの金融機関に対する法務アドバイス、および多様な業種の企業法務・コンプライアンス支援を行っています。
              </p>
              <p>
                契約書の作成・検討、取引構造の法的リスク分析、株主・取締役関連の紛争など、企業の運営を支えるための総合的なサポートを提供します。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">金融・投資関連の紛争と規制対応</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                金融取引や投資に関する紛争は、複雑な契約構造や国際的な規制を伴うことが多く、迅速かつ専門的な対応が求められます。
              </p>
              <p>
                輝相（フィサン）は、金融取引・投資トラブルの解決だけでなく、金融監督当局への対応、規制リスクへの事前予防策の立案にも注力しています。
              </p>
              <p>
                企業が自らの信頼性と透明性を維持できるよう、現実的かつ実務的な観点から最適な解決策を提示いたします。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">日韓ビジネスの橋渡しとして</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                日本企業や投資家にとって、韓国での法的手続や企業経営は、言語や文化の違いから予想以上に複雑に感じられることがあります。
              </p>
              <p>
                輝相（フィサン）の弁護士は、日韓両国の法律制度・企業文化・交渉スタイルを理解しており、日本人依頼者が安心して韓国でビジネスを展開できるよう支援いたします。
              </p>
              <p>
                法的安定性の確保はもちろん、信頼できる現地パートナーとして、あなたの企業の成長と持続的な成功を力強くサポートします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            金融・企業法務でお困りの方は、<br />お気軽にご相談ください。
          </p>
          <div className="space-y-4">
            <a href="tel:010-4729-3573" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">010-4729-3573</p>
            </a>
            <a href="mailto:mgkim@hwisang.co.kr" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">mgkim@hwisang.co.kr</p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">法務法人フィサン</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                韓国を代表する法律事務所として、<br />
                専門的で信頼できる法律サービスを提供します
              </p>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">業務分野</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>刑事事件</li>
                <li>金融・不動産</li>
                <li>経営権紛争</li>
                <li>家事事件</li>
              </ul>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">お問い合わせ</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>電話: 010-4729-3573</li>
                <li>Email: mgkim@hwisang.co.kr</li>
                <li>住所: ソウル特別市江南区永東大路611, 13階</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-8 text-center">
            <p className="text-xs text-gray-500">&copy; 2024 法務法人フィサン. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
